import {
  postExcelLangfuseObservation,
  type ExcelLangfusePhase,
  type ExcelLangfuseRunContext,
  type ExcelUnifiedAgentExecutorMode,
} from "./langfuseTrace";
import type { ToolCallDetailedResult } from "./toolRegistry";
import {
  summarizeForToolObservation,
  truncateToolObservationPreview,
} from "../shared/services/toolObservationFormat";

export interface ExcelToolObservationOptions {
  context?: ExcelLangfuseRunContext;
  phase: ExcelLangfusePhase;
  /** Executor architecture, when emitted by a unified-agent tool loop. */
  executorMode?: ExcelUnifiedAgentExecutorMode;
  toolName: string;
  spanKey?: string;
  args: unknown;
  result: ToolCallDetailedResult;
  startedAtMs: number;
  endedAtMs?: number;
  success?: boolean;
  parsedOutput?: unknown;
}

const LOCAL_PATH_KEY_PATTERN =
  /^(filePath|folderPath|fullFileName|filename|fullName|path)$/i;
const LOCAL_WORKBOOK_PATH_TEXT_PATTERN =
  /(?:[A-Za-z]:[\\/]|\\\\)[^"\r\n]*?\.(?:xlsx|xlsm|xlsb|xls|csv)\b/gi;

function redactLocalPaths(value: unknown): unknown {
  const localPaths = new Set<string>();
  const collect = (candidate: unknown): void => {
    if (!candidate || typeof candidate !== "object") return;
    if (Array.isArray(candidate)) {
      candidate.forEach(collect);
      return;
    }
    for (const [key, nested] of Object.entries(candidate as Record<string, unknown>)) {
      if (
        LOCAL_PATH_KEY_PATTERN.test(key) &&
        typeof nested === "string" &&
        nested.length > 0
      ) {
        localPaths.add(nested);
      }
      collect(nested);
    }
  };
  collect(value);

  const visit = (candidate: unknown): unknown => {
    if (typeof candidate === "string") {
      let sanitized = candidate.replace(
        LOCAL_WORKBOOK_PATH_TEXT_PATTERN,
        "<local-path>",
      );
      for (const localPath of localPaths) {
        sanitized = sanitized.replaceAll(localPath, "<local-path>");
      }
      return sanitized;
    }
    if (Array.isArray(candidate)) return candidate.map(visit);
    if (!candidate || typeof candidate !== "object") return candidate;
    return Object.fromEntries(
      Object.entries(candidate as Record<string, unknown>).map(([key, nested]) => [
        key,
        LOCAL_PATH_KEY_PATTERN.test(key) && typeof nested === "string"
          ? "<local-path>"
          : visit(nested),
      ]),
    );
  };
  return visit(value);
}

function parseToolOutput(output: string): unknown {
  try {
    return JSON.parse(output);
  } catch {
    return output;
  }
}

function formatToolArgsPreview(args: unknown): string {
  if (typeof args === "string") {
    return truncateToolObservationPreview(args);
  }
  if (args == null) return "";
  try {
    return truncateToolObservationPreview(JSON.stringify(redactLocalPaths(args)));
  } catch {
    return truncateToolObservationPreview(String(args));
  }
}

function getToolErrorMessage(parsedOutput: unknown, rawOutput: string): string | undefined {
  if (parsedOutput && typeof parsedOutput === "object") {
    const output = parsedOutput as Record<string, unknown>;
    const candidate = [output.error, output.message, output.statusMessage, output.reason].find(
      (value) => typeof value === "string" && value.trim().length > 0,
    );
    if (typeof candidate === "string") {
      return truncateToolObservationPreview(candidate.trim());
    }
  }
  if (typeof parsedOutput === "string" && parsedOutput.trim()) {
    return truncateToolObservationPreview(parsedOutput.trim());
  }
  if (rawOutput.trim()) {
    return truncateToolObservationPreview(rawOutput.trim());
  }
  return undefined;
}

function inferToolSuccess(parsedOutput: unknown): boolean {
  if (!parsedOutput || typeof parsedOutput !== "object") return true;
  const output = parsedOutput as { ok?: unknown; error?: unknown };
  return output.ok !== false && !output.error;
}

/**
 * Posts a bounded Langfuse observation for an Excel tool call.
 *
 * Both Excel tool loops use this helper so trace payload shape, truncation, and
 * trace-only FactSet correlation metadata stay consistent.
 */
export function postExcelToolObservation(options: ExcelToolObservationOptions): void {
  // finster_query is traced by the server proxy, which owns the real TOOL span
  // and forwards that exact span id to data-feed as the trusted W3C parent.
  if (options.toolName === "finster_query" || options.toolName === "factset_ai_query") return;
  const endedAtMs = options.endedAtMs ?? Date.now();
  const elapsedMs = Math.max(0, endedAtMs - options.startedAtMs);
  const rawOutput = options.result.output;
  const parsedOutput = redactLocalPaths(
    options.parsedOutput ?? parseToolOutput(rawOutput),
  );
  const success = options.success ?? inferToolSuccess(parsedOutput);
  const errorMessage = success
    ? undefined
    : getToolErrorMessage(parsedOutput, rawOutput) ?? "Tool call failed without an error payload.";

  postExcelLangfuseObservation(
    options.context,
    {
      name: `tool:${options.toolName}`,
      ...(options.spanKey ? { spanKey: options.spanKey } : {}),
      input: {
        tool: options.toolName,
        argsPreview: formatToolArgsPreview(options.args),
      },
      output: {
        success,
        ...(errorMessage ? { error: errorMessage } : {}),
        ...(parsedOutput !== undefined ? { data: summarizeForToolObservation(parsedOutput) } : {}),
        referenceImageCount: options.result.referenceImages?.length ?? 0,
      },
      metadata: {
        tool: options.toolName,
        success,
        durationMs: elapsedMs,
        ...(options.executorMode
          ? { executorMode: options.executorMode }
          : {}),
        ...(options.result.observationMetadata ?? {}),
      },
      level: success ? "DEFAULT" : "ERROR",
      ...(errorMessage ? { statusMessage: errorMessage } : {}),
      startTime: new Date(options.startedAtMs).toISOString(),
      endTime: new Date(endedAtMs).toISOString(),
    },
    { phase: options.phase },
  );
}
